import {
  NOTIFICATION_CENTER_DEADLINES_TAB_ID,
  NOTIFICATION_CENTER_TAB_KEYS,
} from "../../../redux/features/notification-center/notificationCenterConstants";
import {
  normalizeNotificationCenterFilterStateForTab,
  normalizeNotificationCenterFiltersByTab,
  pickNotificationCenterPersistedFilters,
} from "../config/notificationCenterFilterConfig";

export const NOTIFICATION_CENTER_FILTERS_STORAGE_KEY = "notification_center_filters_param";

/**
 * Browser-wide filter preferences per tab (shared across accounts in this browser).
 */
const LEGACY_NOTIFICATION_CENTER_FILTERS_STORAGE_KEYS = [
  "lovat_notification_center_filters_browser",
  "lovat_notification_center_filters",
];

function isLegacyFlatFiltersPayload(parsed) {
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return false;
  }
  if (parsed.byTab && typeof parsed.byTab === "object") {
    return false;
  }
  return (
    parsed.visibilityFilter !== undefined ||
    parsed.deadlinesScopeFilter !== undefined
  );
}

function migrateLegacyFlatFiltersPayload(parsed) {
  /** @type {Record<string, Record<string, string>>} */
  const byTab = {};

  for (const tabId of NOTIFICATION_CENTER_TAB_KEYS) {
    byTab[tabId] = normalizeNotificationCenterFilterStateForTab(tabId, {
      visibilityFilter: parsed.visibilityFilter,
      ...(tabId === NOTIFICATION_CENTER_DEADLINES_TAB_ID
        ? { deadlinesScopeFilter: parsed.deadlinesScopeFilter }
        : {}),
    });
  }

  return byTab;
}

function parseStoredFiltersPayload(parsed) {
  if (isLegacyFlatFiltersPayload(parsed)) {
    return migrateLegacyFlatFiltersPayload(parsed);
  }

  const source =
    parsed?.byTab && typeof parsed.byTab === "object" ? parsed.byTab : parsed;

  return normalizeNotificationCenterFiltersByTab(source);
}

/**
 * @returns {Record<string, Record<string, string>>}
 */
export function loadNotificationCenterFiltersFromStorage() {
  if (typeof window === "undefined") {
    return normalizeNotificationCenterFiltersByTab();
  }

  try {
    let raw = window.localStorage.getItem(NOTIFICATION_CENTER_FILTERS_STORAGE_KEY);
    if (!raw) {
      for (const legacyKey of LEGACY_NOTIFICATION_CENTER_FILTERS_STORAGE_KEYS) {
        const legacyRaw = window.localStorage.getItem(legacyKey);
        if (legacyRaw) {
          window.localStorage.setItem(NOTIFICATION_CENTER_FILTERS_STORAGE_KEY, legacyRaw);
          window.localStorage.removeItem(legacyKey);
          raw = legacyRaw;
          break;
        }
      }
    }

    if (!raw) {
      return normalizeNotificationCenterFiltersByTab();
    }

    return parseStoredFiltersPayload(JSON.parse(raw));
  } catch {
    return normalizeNotificationCenterFiltersByTab();
  }
}

/**
 * @param {{ filtersByTab?: Record<string, Record<string, unknown>> }} state
 */
export function applyStoredNotificationCenterFiltersToState(state) {
  state.filtersByTab = loadNotificationCenterFiltersFromStorage();
}

/**
 * @param {Record<string, Record<string, unknown>>} filtersByTab
 */
export function saveNotificationCenterFiltersToStorage(filtersByTab) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      NOTIFICATION_CENTER_FILTERS_STORAGE_KEY,
      JSON.stringify({
        byTab: pickNotificationCenterPersistedFilters(filtersByTab ?? {}),
      })
    );
  } catch {
    // ignore quota / private browsing
  }
}
